import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

export default function AcademyCourse() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [lessons, setLessons] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("access_token") || localStorage.getItem("token") || "";

  useEffect(() => {
    fetch(`/api/academy/courses/${slug}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json()).then(res => {
      if (res.ok) setData(res.data);
      setLoading(false);
    });
  }, [slug]);

  const loadLessons = async (moduleId) => {
    if (lessons[moduleId]) {
      setExpanded(expanded === moduleId ? null : moduleId);
      return;
    }
    const res = await fetch(`/api/academy/modules/${moduleId}/lessons`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json());
    if (res.ok) setLessons(prev => ({ ...prev, [moduleId]: res.data }));
    setExpanded(moduleId);
  };

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
      <div style={{ color: "var(--text-muted)", fontSize: 13 }}>Loading...</div>
    </div>
  );

  if (!data) return (
    <div className="page">
      <p style={{ color: "var(--text-muted)" }}>Course not found.</p>
    </div>
  );

  const { course, modules } = data;
  const totalLessons = modules.reduce((a, m) => a + m.lesson_count, 0);
  const completedLessons = modules.reduce((a, m) => a + m.completed, 0);
  const progress = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

  return (
    <div className="page">
      {/* Back */}
      <button onClick={() => navigate("/academy")}
        style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 13, padding: "0 0 16px", display: "flex", alignItems: "center", gap: 6 }}>
        ← Back to Academy
      </button>

      {/* Course Header */}
      <div style={{
        background: "var(--bg-surface)", border: `1px solid var(--border)`,
        borderLeft: `4px solid ${course.color_hex}`,
        borderRadius: "var(--radius-xl)", padding: "24px", marginBottom: 20
      }}>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          <div style={{
            width: 60, height: 60, borderRadius: "var(--radius-lg)",
            background: `${course.color_hex}22`, border: `1px solid ${course.color_hex}44`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 30, flexShrink: 0
          }}>{course.icon}</div>
          <div style={{ flex: 1 }}>
            <h1 style={{ margin: "0 0 6px", fontSize: 20, fontWeight: 800, color: "var(--text-main)" }}>{course.title}</h1>
            <p style={{ margin: "0 0 14px", fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6 }}>{course.description}</p>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 12 }}>
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}> {totalLessons} lessons</span>
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}> {modules.length} modules</span>
              <span style={{ fontSize: 12, color: "#FFD700" }}>   {course.xp_reward} XP</span>
              <span style={{ fontSize: 12, color: progress === 100 ? "var(--success)" : course.color_hex, fontWeight: 700 }}>
                {progress === 100 ? "   Completed!" : `${progress}% complete`}
              </span>
            </div>
            <div style={{ background: "var(--border)", borderRadius: 99, height: 6, overflow: "hidden" }}>
              <div style={{ width: `${progress}%`, height: "100%", background: course.color_hex, borderRadius: 99, transition: "width 0.8s" }} />
            </div>
          </div>
        </div>
      </div>

      {/* Modules */}
      <h2 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700, color: "var(--text-main)" }}>Modules</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {modules.map((mod, idx) => (
          <div key={mod.id} style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-xl)", overflow: "hidden" }}>
            {/* Module Header */}
            <div onClick={() => loadLessons(mod.id)}
              style={{ padding: "16px 20px", cursor: "pointer", display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{
                width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                background: mod.completed === mod.lesson_count && mod.lesson_count > 0
                  ? "var(--success)" : "var(--bg-elevated)",
                border: "1px solid var(--border)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, fontWeight: 700,
                color: mod.completed === mod.lesson_count && mod.lesson_count > 0 ? "#fff" : "var(--text-muted)"
              }}>
                {mod.completed === mod.lesson_count && mod.lesson_count > 0 ? "" : idx + 1}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-main)", marginBottom: 2 }}>{mod.title}</div>
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                  {mod.completed}/{mod.lesson_count} lessons ·   {mod.xp_reward} XP
                </div>
              </div>
              <span style={{ color: "var(--text-muted)", fontSize: 18, transform: expanded === mod.id ? "rotate(90deg)" : "none", transition: "transform 0.2s" }}>›</span>
            </div>

            {/* Lessons List */}
            {expanded === mod.id && lessons[mod.id] && (
              <div style={{ borderTop: "1px solid var(--border)" }}>
                {lessons[mod.id].map((lesson, li) => (
                  <div key={lesson.id}
                    onClick={() => navigate(`/academy/lessons/${lesson.id}`)}
                    style={{
                      padding: "12px 20px 12px 66px", cursor: "pointer",
                      borderBottom: li < lessons[mod.id].length - 1 ? "1px solid var(--border-subtle)" : "none",
                      display: "flex", alignItems: "center", gap: 12,
                      transition: "background 0.1s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "var(--bg-elevated-soft)"}
                    onMouseLeave={e => e.currentTarget.style.background = ""}>
                    <div style={{
                      width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                      background: lesson.completed ? "var(--success)" : "var(--bg-elevated)",
                      border: `1px solid ${lesson.completed ? "var(--success)" : "var(--border)"}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 11, color: lesson.completed ? "#fff" : "var(--text-muted)", fontWeight: 700
                    }}>
                      {lesson.completed ? "" : li + 1}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, color: "var(--text-main)", fontWeight: lesson.completed ? 400 : 600 }}>
                        {lesson.title}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                        {lesson.type} · {lesson.duration} min ·   {lesson.xp_reward} XP
                        {lesson.is_free && <span style={{ marginLeft: 6, color: "var(--success)", fontSize: 10, fontWeight: 700 }}>FREE</span>}
                      </div>
                    </div>
                    <span style={{ color: "var(--text-muted)", fontSize: 16 }}>›</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
